import React, { useState, useEffect, useMemo } from 'react';
import { X, Zap, Check, AlertCircle, Info } from 'react-feather';
import { InlineSelect, InlineCheckbox } from './InlineTable';
import { ToolbarButton } from './ConfigPages';
import { getAiRevenueCoaMappingSuggestions } from '../utils/api';

/**
 * Modal component for AI-powered revenue type to COA mapping
 * Allows users to preview AI suggestions and selectively apply mappings
 */
const AiRevenueCoaMappingModal = ({
  isOpen,
  onClose,
  revenueTypes,
  chartAccounts,
  orgId,
  locationId,
  onApplyMappings
}) => {
  const [includeAlreadyMapped, setIncludeAlreadyMapped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [unmappedTypes, setUnmappedTypes] = useState([]);
  const [selectedMappings, setSelectedMappings] = useState(new Set());
  const [metrics, setMetrics] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  // Every live revenue type is a candidate — including ones with a blank
  // mappedType (those are exactly the ones most in need of a suggestion; the
  // AI works from the name when the type bucket is empty). The toggle widens
  // the set to types that already carry a COA (for remapping).
  const typesToMap = useMemo(() => {
    if (includeAlreadyMapped) return revenueTypes;
    return revenueTypes.filter(rt => !rt.chartOfAccountId);
  }, [revenueTypes, includeAlreadyMapped]);

  // COA options for manual override
  const coaOptions = useMemo(() => {
    return chartAccounts.map(ca => ({
      value: ca.id,
      label: `${ca.accountNumber} - ${ca.accountName}`
    }));
  }, [chartAccounts]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      setUnmappedTypes([]);
      setSelectedMappings(new Set());
      setError(null);
      setMetrics(null);
      setHasRun(false);
    }
  }, [isOpen]);

  const handleRunAiMapping = async () => {
    if (typesToMap.length === 0) {
      setError('No revenue types to map. All types may already be mapped.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);
    setUnmappedTypes([]);

    try {
      const revenueTypesData = typesToMap.map(rt => ({
        id: rt.id,
        name: rt.name,
        mappedType: rt.mappedType || ''
      }));

      const chartAccountsData = chartAccounts.map(ca => ({
        id: ca.id,
        accountNumber: ca.accountNumber,
        accountName: ca.accountName
      }));

      const result = await getAiRevenueCoaMappingSuggestions({
        orgId,
        locationId,
        revenueTypes: revenueTypesData,
        chartAccounts: chartAccountsData
      });

      if (result.success) {
        const suggestionsWithState = (result.mappings || []).map(m => ({
          ...m,
          selectedChartOfAccountId: m.chartOfAccountId
        }));
        setSuggestions(suggestionsWithState);
        setUnmappedTypes(result.unmappedTypes || []);
        setMetrics(result.metrics);

        // Auto-select high-confidence mappings (>= 0.7)
        const highConfidence = new Set(
          suggestionsWithState
            .filter(m => m.confidence >= 0.7)
            .map(m => m.revenueTypeId)
        );
        setSelectedMappings(highConfidence);
        setHasRun(true);
      } else {
        setError(result.error || 'AI mapping failed. Please try again.');
      }
    } catch (err) {
      console.error('Error running AI mapping:', err);
      setError(err.message || 'Failed to get AI suggestions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMapping = (revenueTypeId) => {
    setSelectedMappings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(revenueTypeId)) {
        newSet.delete(revenueTypeId);
      } else {
        newSet.add(revenueTypeId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedMappings(new Set(suggestions.map(m => m.revenueTypeId)));
  };

  const deselectAll = () => {
    setSelectedMappings(new Set());
  };

  const handleCoaChange = (revenueTypeId, newCoaId) => {
    setSuggestions(prev =>
      prev.map(m =>
        m.revenueTypeId === revenueTypeId
          ? { ...m, selectedChartOfAccountId: newCoaId }
          : m
      )
    );
  };

  const handleApplyMappings = () => {
    const mappingsToApply = suggestions
      .filter(m => selectedMappings.has(m.revenueTypeId))
      .map(m => ({
        revenueTypeId: m.revenueTypeId,
        chartOfAccountId: m.selectedChartOfAccountId
      }));

    onApplyMappings(mappingsToApply);
  };

  const confidenceTone = (confidence) => (confidence >= 0.8 ? 'cp-ok' : confidence >= 0.6 ? 'cp-warn' : 'cp-info');

  if (!isOpen) return null;

  return (
    <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cp-modal cp-wide" role="dialog" aria-label="AI map revenue types to COA" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="cp-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="cp-tag cp-orange" style={{ height: 32, width: 32, justifyContent: 'center', padding: 0, borderRadius: 8 }}><Zap size={16} /></span>
            <div>
              <h2>AI map COA</h2>
              <div className="cp-help">Suggests a chart of accounts entry for each revenue type from its name and type bucket (USALI conventions).</div>
            </div>
          </div>
          <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={onClose} aria-label="Close" />
        </div>

        {/* Content */}
        <div className="cp-modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {!hasRun && (
            <div className="cp-note" style={{ marginBottom: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <InlineCheckbox checked={includeAlreadyMapped} onChange={(e) => setIncludeAlreadyMapped(e.target.checked)} />
                <span>Include types that already have a COA (for remapping)</span>
              </label>
              <span className="cp-mono">{typesToMap.length} revenue type{typesToMap.length !== 1 ? 's' : ''} to map</span>
            </div>
          )}

          {!hasRun && !isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, alignItems: 'center' }}>
              <Info size={14} style={{ color: 'var(--lodge-onyx-400)' }} />
              <ToolbarButton variant="primary" icon={Zap} onClick={handleRunAiMapping} disabled={typesToMap.length === 0}>Generate suggestions</ToolbarButton>
            </div>
          )}

          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', gap: 8 }}>
              <div className="animate-spin" style={{ width: 40, height: 40, borderRadius: '50%', border: '4px solid var(--lodge-orange-400)', borderTopColor: 'transparent' }} />
              <p style={{ fontSize: 14, color: 'var(--lodge-onyx-700)', margin: '8px 0 0' }}>Analyzing revenue types and generating suggestions…</p>
              <p className="cp-help">This may take a few moments</p>
            </div>
          )}

          {error && (
            <div className="cp-note cp-warn" role="alert"><AlertCircle size={16} /><span>{error}</span></div>
          )}

          {hasRun && !isLoading && suggestions.length > 0 && (
            <>
              {metrics && (
                <div className="cp-help" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span>{suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}{unmappedTypes.length > 0 && ` (${unmappedTypes.length} could not be mapped)`}</span>
                  <span className="cp-mono">Cost: ${metrics.estimatedCost?.toFixed(4) || '0.0000'}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <ToolbarButton small variant="ghost" onClick={selectAll}>Select all</ToolbarButton>
                <ToolbarButton small variant="ghost" onClick={deselectAll}>Deselect all</ToolbarButton>
                <span className="cp-meta">{selectedMappings.size} selected</span>
              </div>

              <div className="it-frame">
                <div className="it-scroll" style={{ '--it-max-h': '48vh' }}>
                  <table className="it-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 48, textAlign: 'center' }}>Use</th>
                        <th>Revenue type</th>
                        <th style={{ width: 340 }}>Suggested COA</th>
                        <th style={{ width: 110 }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((suggestion) => {
                        const isSelected = selectedMappings.has(suggestion.revenueTypeId);
                        return (
                          <tr key={suggestion.revenueTypeId} className={`cp-row${isSelected ? ' it-row-selected' : ''}`}>
                            <td style={{ textAlign: 'center' }}>
                              <InlineCheckbox checked={isSelected} onChange={() => toggleMapping(suggestion.revenueTypeId)} />
                            </td>
                            <td style={{ height: 'auto', padding: '8px 0' }}>
                              <div className="cp-cell">
                                <div className="cp-name">{suggestion.revenueTypeName}</div>
                                {suggestion.reasoning && <div className="cp-help" style={{ marginTop: 2 }}>{suggestion.reasoning}</div>}
                              </div>
                            </td>
                            <td>
                              <InlineSelect
                                value={suggestion.selectedChartOfAccountId ?? null}
                                options={coaOptions}
                                onChange={(v) => handleCoaChange(suggestion.revenueTypeId, v ?? undefined)}
                                placeholder="Select COA…"
                                rowActive
                                minWidth={300}
                                menuZIndex={100000}
                                ariaLabel="Chart of account"
                              />
                            </td>
                            <td>
                              <span className={`cp-tag cp-mono ${confidenceTone(suggestion.confidence)}`} style={{ marginLeft: 12 }}>
                                {Math.round(suggestion.confidence * 100)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {unmappedTypes.length > 0 && (
                <div className="cp-note cp-warn" style={{ marginTop: 12, alignItems: 'flex-start' }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{unmappedTypes.length} revenue type{unmappedTypes.length !== 1 ? 's' : ''} could not be mapped</div>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                      {unmappedTypes.map((ut) => (
                        <li key={ut.revenueTypeId}><strong>{ut.revenueTypeName}</strong>: {ut.reason}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {hasRun && !isLoading && suggestions.length === 0 && !error && (
            <div className="cp-empty">
              <h3>No suggestions could be generated</h3>
              {unmappedTypes.length > 0 && <p>{unmappedTypes.length} revenue type{unmappedTypes.length !== 1 ? 's' : ''} could not be matched.</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cp-modal-foot" style={{ justifyContent: 'space-between' }}>
          <ToolbarButton variant="ghost" onClick={onClose}>Cancel</ToolbarButton>
          {hasRun && suggestions.length > 0 && (
            <ToolbarButton variant="primary" icon={Check} onClick={handleApplyMappings} disabled={selectedMappings.size === 0}>
              Apply {selectedMappings.size} mapping{selectedMappings.size !== 1 ? 's' : ''}
            </ToolbarButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiRevenueCoaMappingModal;
